import { Modal } from 'antd';
import { Stream } from '@cloudflare/stream-react';
import styled from '@emotion/styled';

interface VideoPreviewModalProps {
  visible: boolean;
  fileUrl: string;
  fileName?: string;
  onClose: () => void;
  isStreaming?: boolean;
}

interface VideoPlayerProps {
  fileUrl: string;
  isStreaming: boolean;
}

const StreamWrapper = styled.div`
  height: 50vh;

  .cloudflare-stream-player {
    height: 50vh;
    padding-top: 0 !important;
  }
`;

const CloudflareStreamPlayer = ({ streamId }: { streamId: string }) => (
  <StreamWrapper>
    <Stream controls src={streamId} loop autoplay className="cloudflare-stream-player" />
  </StreamWrapper>
);

const RegularVideoPlayer = ({ fileUrl }: { fileUrl: string }) => (
  <video
    src={fileUrl}
    controls
    autoPlay
    style={{
      width: '100%',
      height: 'auto',
      maxHeight: '70vh',
      display: 'block',
    }}
  >
    Your browser does not support the video tag.
  </video>
);

const StreamingVideoPlayer = ({ fileUrl }: { fileUrl: string }) => (
  <video
    src={fileUrl}
    controls
    autoPlay
    style={{
      width: '100%',
      height: 'auto',
      maxHeight: '70vh',
      display: 'block',
    }}
  >
    Your browser does not support this video format.
  </video>
);

export const VideoPlayer = ({ fileUrl, isStreaming }: VideoPlayerProps) => {
  // Extract stream ID from URL if it's a Cloudflare Stream URL
  const getStreamId = (url: string): string => {
    // Handle different Cloudflare Stream URL formats
    const streamIdMatch = url.match(/\/([a-f0-9]{32})\/|stream\/([a-f0-9]{32})/i);
    return streamIdMatch ? streamIdMatch[1] || streamIdMatch[2] : url;
  };

  if (isStreaming) {
    const streamId = getStreamId(fileUrl);

    // If it looks like a Cloudflare Stream ID, use the Stream component
    if (/^[a-f0-9]{32}$/i.test(streamId)) {
      return <CloudflareStreamPlayer streamId={streamId} />;
    }

    // For other streaming formats like m3u8, use video element
    return <StreamingVideoPlayer fileUrl={fileUrl} />;
  }

  // Regular video files
  return <RegularVideoPlayer fileUrl={fileUrl} />;
};

const VideoPreviewModal = ({
  visible,
  fileUrl,
  fileName,
  onClose,
  isStreaming = false,
}: VideoPreviewModalProps) => {
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width="80%"
      style={{ maxWidth: '800px' }}
      title={fileName ? `Video Preview - ${fileName}` : 'Video Preview'}
    >
      <VideoPlayer fileUrl={fileUrl} isStreaming={isStreaming} />
    </Modal>
  );
};

export default VideoPreviewModal;
